import { z } from 'zod';

import { idSchema, instantSchema, paginatedSchema } from './common';
import { notificationChannelSchema, notificationTypeSchema } from './enums';
import { LIMITS } from './limits';

/** Notifications (api-design §3.8, NTF-1..5). */

export const notificationSchema = z.object({
  id: idSchema,
  type: notificationTypeSchema,
  /** Null for reminders (sent by the system). */
  actorId: idSchema.nullable(),
  projectId: idSchema,
  taskId: idSchema.nullable(),
  commentId: idSchema.nullable(),
  /** Snapshot at the time: task key and title, project name, comment excerpt. */
  payload: z.object({
    taskKey: z.string().optional(),
    taskTitle: z.string().optional(),
    projectName: z.string().optional(),
    excerpt: z.string().optional(),
    dueDate: z.string().optional(),
  }),
  createdAt: instantSchema,
  readAt: instantSchema.nullable(),
});

export const notificationsPageSchema = paginatedSchema(notificationSchema);

export const notificationsQuerySchema = z.object({
  unread: z.stringbool().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
  cursor: z.string().min(1).max(200).optional(),
});

export const unreadCountSchema = z.object({ count: z.number().int().min(0) });

export const notificationPreferenceSchema = z.object({
  type: notificationTypeSchema,
  channel: notificationChannelSchema,
  enabled: z.boolean(),
});

/** NTF-5: every event × channel, and the projects the person muted. */
export const notificationSettingsSchema = z.object({
  preferences: z.array(notificationPreferenceSchema),
  mutedProjectIds: z.array(idSchema),
});

export const updateNotificationSettingsRequestSchema = z.object({
  preferences: z.array(notificationPreferenceSchema).max(20),
});

export type Notification = z.infer<typeof notificationSchema>;
export type NotificationsPage = z.infer<typeof notificationsPageSchema>;
export type NotificationSettings = z.infer<typeof notificationSettingsSchema>;
export type NotificationPreference = z.infer<typeof notificationPreferenceSchema>;

// ── Remind me (D47, NTF-6, MYW-5) ────────────────────────────────────────────────────────────────

/** A note is optional: blank means none. */
const reminderNoteSchema = z
  .string()
  .trim()
  .max(LIMITS.reminderNoteMax)
  .transform((note) => (note === '' ? null : note));

/** `PUT /tasks/{id}/reminder`: when (an instant, not in the past) and why. */
export const setReminderRequestSchema = z.object({
  remindAt: instantSchema,
  note: reminderNoteSchema.nullable().optional(),
});

/** Your reminder on one work item; nobody else's is ever shown. */
export const reminderSchema = z.object({
  taskId: idSchema,
  remindAt: instantSchema,
  note: z.string().nullable(),
});

/** `GET /tasks/{id}/reminder`: yours on this work item, or none. */
export const taskReminderResponseSchema = z.object({ reminder: reminderSchema.nullable() });

/** `GET /me/reminders` (MYW-5): your coming reminders, soonest first, on work items you can see. */
export const myRemindersResponseSchema = z.object({
  items: z.array(
    reminderSchema.extend({
      task: z.object({ id: idSchema, key: z.string(), title: z.string() }),
      project: z.object({ id: idSchema, name: z.string() }),
    }),
  ),
});

export type SetReminderRequest = z.infer<typeof setReminderRequestSchema>;
export type Reminder = z.infer<typeof reminderSchema>;
export type TaskReminderResponse = z.infer<typeof taskReminderResponseSchema>;
export type MyReminders = z.infer<typeof myRemindersResponseSchema>;
export type MyReminder = MyReminders['items'][number];

// ── Push (D46, ADR-0015) ─────────────────────────────────────────────────────────────────────────

/** `PUT /auth/phones/current/push-token`: the phone's Firebase Cloud Messaging registration token. */
export const pushTokenRequestSchema = z.object({ token: z.string().trim().min(1).max(4096) });

export type PushTokenRequest = z.infer<typeof pushTokenRequestSchema>;
